import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, Send } from 'lucide-react';
import { trackerFdfcDispatchProblems, type TrackerFdfcDetail } from '@tracker/shared';
import { trackerFdfcService } from '@/services/tracker-fdfc.service';
import { getErrorMessage } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { KitPicker } from './KitPicker';

/**
 * One more NSDC kit order for a person who already has one — a damaged kit, a
 * cancelled or lost parcel. A real order: NSDC does not de-duplicate, so the
 * server claims the entry first and a second click cannot send twice.
 */
export function DispatchAnotherDialog({
  entry,
  open,
  onClose,
}: {
  entry: TrackerFdfcDetail;
  open: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [kitCode, setKitCode] = useState('');

  // Address problems NSDC would refuse (the dispatch state itself is checked by the server).
  const problems = trackerFdfcDispatchProblems({ ...entry, dispatch_status: 'not_dispatched' });

  const send = useMutation({
    mutationFn: () => trackerFdfcService.dispatchAgain(entry.id, kitCode),
    onSuccess: (r) => {
      toast.success(`Order ${r.order_code} created at NSDC`);
      void queryClient.invalidateQueries({ queryKey: ['tracker-fdfc'] });
      close(true);
    },
    // Shown in the dialog; the entry may have changed state (no answer → needs checking).
    onError: () => void queryClient.invalidateQueries({ queryKey: ['tracker-fdfc'] }),
  });

  const close = (force = false) => {
    if (send.isPending && !force) return;
    setKitCode('');
    send.reset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Dispatch another order</DialogTitle>
          <DialogDescription>
            {entry.full_name ?? '—'} · {entry.mobile_last10}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <KitPicker enabled={open} kitCode={kitCode} onChange={setKitCode} label="Kit to send" />

          {problems.length > 0 && (
            <div className="rounded-lg border border-rose-300/60 bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-500/10 dark:text-rose-300">
              <p className="font-medium">Fix the recipient first — NSDC will refuse:</p>
              <ul className="mt-1 list-disc pl-5">
                {problems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </div>
          )}

          <p className="flex items-start gap-2 rounded-lg border border-amber-300/60 bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            This places a real order with NSDC and cannot be undone from here. NSDC does not de-duplicate, so
            sending twice creates two orders.
          </p>

          {send.isError && (
            <p className="rounded-lg border border-rose-300/60 bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-500/10 dark:text-rose-300">
              {getErrorMessage(send.error)}
            </p>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => close()} disabled={send.isPending}>
            Cancel
          </Button>
          <Button onClick={() => send.mutate()} disabled={!kitCode || problems.length > 0 || send.isPending}>
            {send.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Creating order…
              </>
            ) : (
              <>
                <Send className="h-4 w-4" /> Create order
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
